import { apiClient, ApiResponse, User } from './apiClient';

// Enhanced admin interfaces
export interface AdminUser extends User {
  permissions: string[];
  lastLoginAt: string;
  loginCount: number;
  isActive: boolean;
  createdBy?: string;
  department?: string;
  notes?: string;
  Role: {
    id: number;
    name: string;
    description: string;
    permissions: Permission[];
  };
}

export interface Permission {
  id: string;
  name: string;
  description: string;
  resource: string;
  action: string;
  module: string;
}

export interface AdminDashboardStats {
  overview: {
    total_users: number;
    total_products: number;
    total_orders: number;
    total_revenue: number;
    active_orders: number;
    pending_reviews: number;
    system_uptime: number;
    last_update: string;
  };
  growth_metrics: {
    user_growth: {
      current_period: number;
      previous_period: number;
      growth_rate: number;
    };
    revenue_growth: {
      current_period: number;
      previous_period: number;
      growth_rate: number;
    };
    order_growth: {
      current_period: number;
      previous_period: number;
      growth_rate: number;
    };
  };
  financial_summary: {
    total_revenue: number;
    pending_payments: number;
    refunds_issued: number;
    commission_earned: number;
    payment_methods: {
      [key: string]: {
        count: number;
        total: number;
      };
    };
  };
  operational_metrics: {
    average_order_value: number;
    cancellation_rate: number;
    total_orders_by_status: {
      [key: string]: {
        count: number;
        avg_amount: number;
      };
    };
  };
  user_metrics: {
    by_role: Array<{
      role: string;
      count: number;
      last_signup: string;
    }>;
  };
  product_metrics: {
    by_category: Array<{
      category: string;
      category_ar: string;
      count: number;
      avg_price: number;
    }>;
    by_brand: Array<{
      brand: string;
      brand_ar: string;
      count: number;
      avg_price: number;
    }>;
    top_performers: Array<{
      id: string;
      name: string;
      booking_count: number;
      total_revenue: number;
    }>;
  };
  review_metrics: {
    average_rating: number;
    total_reviews: number;
    positive_reviews: number;
    negative_reviews: number;
    satisfaction_rate: number;
  };
  recent_activity: {
    latest_orders: Array<{
      order_id: string;
      amount: number;
      status: string;
      created_at: string;
      user: {
        id: string;
        name: string;
        email: string;
      };
      products: Array<{
        id: string;
        name: string;
        quantity: number;
        subtotal: number;
      }>;
    }>;
  };
  payment_metrics: {
    total_transactions: number;
    successful_payments: number;
    failed_payments: number;
    pending_payments: number;
    payment_success_rate: number;
    by_status: {
      [key: string]: {
        count: number;
        total_amount: number;
      };
    };
    by_provider: Array<{
      provider_name?: string;
      provider_code?: string;
      total_transactions: number;
      successful_transactions: number;
      success_rate: number;
      successful_amount: number;
    }>;
  };
  recent_activities?: AdminActivity[];
  alerts?: AdminAlert[];
}

export interface AdminActivity {
  type: 'new_booking' | 'user_registration' | 'review_submitted' | 'payment_completed' | 'property_created';
  description: string;
  amount?: number;
  rating?: number;
  timestamp: string;
  user_id?: string;
  booking_id?: string;
  property_id?: string;
}

export interface AdminAlert {
  id: string;
  type: 'payment_failed' | 'new_review' | 'system_error' | 'low_occupancy' | 'pending_moderation';
  priority: 'low' | 'medium' | 'high' | 'critical';
  title: string;
  message: string;
  data: Record<string, any>;
  action_url?: string;
  is_read: boolean;
  created_at: string;
}

export interface ExtendedUser {
  id: string;
  name: string;
  phone: string;
  email: string;
  language: 'en' | 'ar';
  roleId: 1 | 2 | 3;
  Role: {
    id: number;
    name: string;
    description: string;
  };
  isActive: boolean;
  lastLoginAt?: string;
  createdAt: string;
  totalBookings: number;
  totalSpent: number;
  CreatedProperties: any[];
  UserReviews: any[];
}

export interface ExtendedProperty {
  id: number;
  name: string;
  description: string;
  type_id: number;
  PropertyType: {
    name_en: string;
    name_ar: string;
  };
  location: string;
  full_price: string;
  calculated_deposit: string;
  is_available: boolean;
  is_active: boolean;
  featured: boolean;
  rating: string;
  reviews_count: number;
  total_bookings: number;
  total_revenue: number;
  occupancy_rate: number;
  last_booked?: string;
  Creator: {
    id: string;
    name: string;
    Role: { name: string };
  };
  Media: any[];
  Bookings: any[];
  Reviews: any[];
  createdAt: string;
  updatedAt: string;
}

export interface ExtendedBooking {
  id: string;
  userId: string;
  property_id: number;
  startDate: string;
  endDate: string;
  status: 'pending' | 'confirmed' | 'cancelled' | 'completed';
  payment_status: 'pending' | 'partial' | 'paid' | 'refunded';
  total_price: string;
  deposit_amount: string;
  deposit_paid: string;
  remaining_amount: string;
  guest_count: number;
  special_requests?: string;
  couponCode?: string;
  discountAmount?: string;
  finalAmount?: string;
  payment_method?: string;
  User: {
    id: string;
    name: string;
    phone: string;
    email: string;
  };
  PropertyBooking: {
    id: number;
    name: string;
    location: string;
    PropertyType: { name_en: string };
  };
  Coupon?: {
    id: number;
    code: string;
    type: string;
    value: string;
  };
  PaymentHistory: PaymentRecord[];
  createdAt: string;
  check_in: string;
  check_out: string;
}

export interface PaymentRecord {
  id: string;
  amount: string;
  method: string;
  status: 'pending' | 'completed' | 'failed' | 'refunded';
  transaction_id?: string;
  created_at: string;
}

export interface ExtendedReview {
  id: string;
  bookingId: string;
  propertyId: number;
  userId: string;
  rating: number;
  comment: string;
  media: string[];
  isApproved: boolean;
  isFlagged: boolean;
  adminNotes?: string;
  flagged_reasons: string[];
  moderation_history: ModerationRecord[];
  User: {
    id: string;
    name: string;
    totalReviews: number;
    averageRating: number;
    accountAge: string;
  };
  PropertyReview: {
    id: number;
    name: string;
    location: string;
    currentRating: number;
  };
  Booking: {
    id: string;
    check_in: string;
    check_out: string;
    status: string;
  };
  sentiment_analysis: {
    score: number;
    confidence: number;
    keywords: string[];
  };
  createdAt: string;
}

export interface ModerationRecord {
  action: 'approved' | 'rejected' | 'flagged' | 'unflagged';
  admin_id: string;
  admin_name: string;
  timestamp: string;
  notes?: string;
}

export interface SystemSettings {
  booking_settings: {
    advance_booking_days: number;
    cancellation_window_hours: number;
    auto_confirm_bookings: boolean;
    deposit_required: boolean;
    default_deposit_percentage: number;
  };
  payment_settings: {
    accepted_methods: string[];
    commission_rate: number;
    auto_payout: boolean;
    minimum_payout: number;
  };
  notification_settings: {
    sms_enabled: boolean;
    email_enabled: boolean;
    push_notifications: boolean;
    review_reminders: boolean;
  };
  security_settings: {
    password_policy: {
      min_length: number;
      require_numbers: boolean;
      require_symbols: boolean;
    };
    session_timeout: number;
    max_login_attempts: number;
  };
}

export interface SystemHealth {
  database: {
    status: 'healthy' | 'warning' | 'error';
    connections: number;
    max_connections: number;
    query_performance: 'optimal' | 'slow' | 'critical';
  };
  api_performance: {
    response_time: string;
    error_rate: string;
    requests_per_minute: number;
  };
  storage: {
    used_space: string;
    total_space: string;
    media_files: number;
  };
  third_party_services: {
    payment_gateway: 'online' | 'offline';
    sms_service: 'online' | 'offline';
    email_service: 'online' | 'offline';
    file_storage: 'online' | 'offline';
  };
}

// Admin Service Class
export class AdminService {
  // Enhanced Authentication
  async registerAdmin(data: {
    name: string;
    phone: string;
    email: string;
    password: string;
    language?: 'en' | 'ar';
    department?: string;
    permissions?: string[];
  }): Promise<ApiResponse<{ user: AdminUser; token: string }>> {
    return apiClient.post('/auth/register/admin', data);
  }

  async registerStaff(data: {
    name: string;
    phone: string;
    email: string;
    password: string;
    language?: 'en' | 'ar';
    permissions?: string[];
  }): Promise<ApiResponse<{ user: AdminUser; token: string }>> {
    return apiClient.post('/auth/register/staff', data);
  }

  async checkAdminPermissions(): Promise<boolean> {
    try {
      const response = await apiClient.get('/admin/users');
      return response.success ?? false;
    } catch {
      return false;
    }
  }

  // Dashboard Analytics
  async getDashboardStats(params?: {
    period?: 'daily' | 'weekly' | 'monthly';
    compare_previous?: boolean;
  }): Promise<ApiResponse<AdminDashboardStats>> {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
    }
    const queryString = queryParams.toString();
    const endpoint = queryString ? `/admin/dashboard?${queryString}` : '/admin/dashboard';
    return apiClient.get<AdminDashboardStats>(endpoint);
  }

  // Enhanced User Management
  async getAllUsers(params?: {
    page?: number;
    limit?: number;
    role?: 'customer' | 'admin' | 'staff' | 'all';
    search?: string;
    active?: boolean;
    created_from?: string;
    created_to?: string;
    sort_by?: 'name' | 'created_at' | 'last_login' | 'total_bookings';
    sort_order?: 'asc' | 'desc';
  }): Promise<ApiResponse<{
    users: ExtendedUser[];
    pagination: any;
    stats: {
      total_users: number;
      customers: number;
      staff: number;
      admins: number;
      active_users: number;
      new_this_month: number;
    };
  }>> {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
    }
    return apiClient.get(`/admin/users?${queryParams.toString()}`);
  }

  async updateUser(userId: string, data: {
    name?: string;
    email?: string;
    roleId?: 1 | 2 | 3;
    isActive?: boolean;
    language?: 'en' | 'ar';
    permissions?: string[];
  }): Promise<ApiResponse<ExtendedUser>> {
    return apiClient.patch<ExtendedUser>(`/admin/users/${userId}`, data);
  }

  async deleteUser(userId: string): Promise<ApiResponse<{ userId: string; status: string }>> {
    return apiClient.delete(`/admin/users/${userId}`);
  }

  // Enhanced Property Management
  async getAllPropertiesAdmin(params?: {
    admin?: boolean;
    include_inactive?: boolean;
    page?: number;
    limit?: number;
    type_id?: number;
    location?: string;
    status?: 'active' | 'inactive' | 'pending_approval';
    created_by?: string;
    date_from?: string;
    date_to?: string;
    sort_by?: 'createdAt' | 'revenue' | 'bookings_count' | 'rating';
  }): Promise<ApiResponse<{
    properties: ExtendedProperty[];
    stats: {
      total_properties: number;
      active_properties: number;
      inactive_properties: number;
      featured_properties: number;
      pending_approval: number;
      total_revenue: number;
      average_rating: number;
      total_bookings: number;
    };
  }>> {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
    }
    return apiClient.get(`/properties?${queryParams.toString()}`);
  }

  async updatePropertyStatus(propertyId: number, data: {
    status: 'approved' | 'rejected' | 'pending';
    admin_notes?: string;
    featured?: boolean;
  }): Promise<ApiResponse<ExtendedProperty>> {
    return apiClient.patch<ExtendedProperty>(`/admin/properties/${propertyId}/status`, data);
  }

  async getPropertyStats(params?: {
    period?: 'monthly' | 'yearly';
    property_id?: number;
  }): Promise<ApiResponse<any>> {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
    }
    return apiClient.get(`/properties/admin/stats?${queryParams.toString()}`);
  }

  // Enhanced Booking Management
  async getAllBookingsAdmin(params?: {
    status?: 'pending' | 'confirmed' | 'cancelled' | 'completed' | 'all';
    payment_status?: 'pending' | 'partial' | 'paid' | 'refunded' | 'all';
    user_id?: string;
    property_id?: number;
    date_from?: string;
    date_to?: string;
    sort_by?: 'createdAt' | 'total_price' | 'check_in';
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<ApiResponse<{
    bookings: ExtendedBooking[];
    pagination: any;
    stats: {
      total_bookings: number;
      confirmed_bookings: number;
      pending_bookings: number;
      cancelled_bookings: number;
      completed_bookings: number;
      total_revenue: number;
      pending_payments: number;
      refund_requests: number;
    };
  }>> {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
    }
    return apiClient.get(`/admin/bookings?${queryParams.toString()}`);
  }

  async updateBookingAdmin(bookingId: string, data: {
    status?: 'confirmed' | 'cancelled' | 'completed';
    payment_status?: 'paid' | 'refunded';
    admin_notes?: string;
    refund_amount?: number;
    late_checkout_fee?: number;
  }): Promise<ApiResponse<ExtendedBooking>> {
    return apiClient.patch<ExtendedBooking>(`/admin/bookings/${bookingId}`, data);
  }

  async cancelBookingAdmin(bookingId: string, data: {
    reason: string;
    refund_percentage: number;
    admin_notes?: string;
    notify_customer: boolean;
  }): Promise<ApiResponse<{
    booking_id: string;
    refund_amount: number;
    refund_status: string;
    customer_notified: boolean;
  }>> {
    return apiClient.post(`/admin/bookings/${bookingId}/cancel`, data);
  }

  async exportBookings(params?: {
    start_date?: string;
    end_date?: string;
    status?: string;
    format?: 'csv' | 'xlsx';
  }): Promise<ApiResponse<{ download_url: string }>> {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
    }
    return apiClient.get(`/admin/reports/bookings-csv?${queryParams.toString()}`);
  }

  // Enhanced Review Management
  async getAllReviewsAdmin(params?: {
    status?: 'approved' | 'pending' | 'rejected' | 'all';
    flagged?: 'true' | 'false' | 'all';
    rating?: 1 | 2 | 3 | 4 | 5;
    property_id?: number;
    user_id?: string;
    date_from?: string;
    has_media?: boolean;
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<ApiResponse<{
    reviews: ExtendedReview[];
    moderation_stats: {
      total_reviews: number;
      pending_moderation: number;
      approved_reviews: number;
      rejected_reviews: number;
      flagged_reviews: number;
      reviews_with_media: number;
      average_rating: number;
      sentiment_breakdown: {
        positive: number;
        neutral: number;
        negative: number;
      };
    };
  }>> {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
    }
    return apiClient.get(`/admin/reviews?${queryParams.toString()}`);
  }

  async moderateReview(reviewId: string, data: {
    action: 'approve' | 'reject' | 'flag' | 'unflag';
    admin_notes?: string;
    flag_reasons?: string[];
    notify_user?: boolean;
    public_response?: string;
  }): Promise<ApiResponse<ExtendedReview>> {
    return apiClient.patch<ExtendedReview>(`/admin/reviews/${reviewId}/moderate`, data);
  }

  async bulkModerateReviews(data: {
    review_ids: string[];
    action: 'approve' | 'reject' | 'flag';
    bulk_notes?: string;
  }): Promise<ApiResponse<any>> {
    return apiClient.post('/admin/reviews/bulk-moderate', data);
  }

  async getReviewAnalytics(params?: {
    period?: 'monthly' | 'yearly';
    property_id?: number;
  }): Promise<ApiResponse<any>> {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
    }
    return apiClient.get(`/admin/reviews/analytics?${queryParams.toString()}`);
  }

  async deleteReview(reviewId: string, data: {
    reason: string;
    notify_user: boolean;
    ban_user?: boolean;
  }): Promise<ApiResponse<any>> {
    return apiClient.delete(`/admin/reviews/${reviewId}`, { data });
  }

  // System Management
  async getSystemSettings(): Promise<ApiResponse<SystemSettings>> {
    return apiClient.get<SystemSettings>('/admin/settings');
  }

  async updateSystemSettings(settings: Partial<SystemSettings>): Promise<ApiResponse<SystemSettings>> {
    return apiClient.patch<SystemSettings>('/admin/settings', settings);
  }

  async getSystemHealth(): Promise<ApiResponse<SystemHealth>> {
    return apiClient.get<SystemHealth>('/admin/system/health');
  }

  async createBackup(data: {
    backup_type: 'full' | 'users' | 'properties' | 'bookings';
    include_media: boolean;
    compression: boolean;
  }): Promise<ApiResponse<{ backup_id: string; download_url: string }>> {
    return apiClient.post('/admin/system/backup', data);
  }

  // Alerts and Notifications
  async getAdminAlerts(params?: {
    priority?: 'low' | 'medium' | 'high' | 'critical' | 'all';
    unread?: boolean;
    page?: number;
    limit?: number;
  }): Promise<ApiResponse<{
    alerts: AdminAlert[];
    summary: {
      total_alerts: number;
      unread_alerts: number;
      high_priority: number;
      critical_alerts: number;
    };
  }>> {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
    }
    return apiClient.get(`/admin/alerts?${queryParams.toString()}`);
  }

  async markAlertsRead(data: {
    alert_ids?: string[];
    mark_all?: boolean;
  }): Promise<ApiResponse<any>> {
    return apiClient.patch('/admin/alerts/mark-read', data);
  }

  // Bulk Operations
  async bulkOperation(data: {
    operation: 'export_data' | 'send_notifications' | 'update_prices';
    entity: 'users' | 'properties' | 'bookings' | 'reviews';
    filters: Record<string, any>;
    format?: 'csv' | 'json' | 'xlsx';
    email_to?: string;
  }): Promise<ApiResponse<{ operation_id: string; status: string }>> {
    return apiClient.post('/admin/bulk-operations', data);
  }

  // Analytics Methods
  async getRevenueAnalytics(params?: {
    period?: 'daily' | 'weekly' | 'monthly';
    start_date?: string;
    end_date?: string;
  }): Promise<ApiResponse<any>> {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
    }
    return apiClient.get(`/admin/analytics/revenue?${queryParams.toString()}`);
  }

  async getUserAnalytics(params?: {
    segment?: 'all' | 'new' | 'returning' | 'vip';
    period?: 'monthly' | 'yearly';
  }): Promise<ApiResponse<any>> {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
    }
    return apiClient.get(`/admin/analytics/users?${queryParams.toString()}`);
  }

  // Utility Methods
  formatCurrency(amount: number, currency: string = 'SAR'): string {
    return new Intl.NumberFormat('en-SA', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  }

  formatGrowthRate(current: number, previous: number): { rate: number; trend: 'up' | 'down' | 'stable' } {
    if (previous === 0) return { rate: 0, trend: 'stable' };
    
    const rate = ((current - previous) / previous) * 100;
    const trend = rate > 0 ? 'up' : rate < 0 ? 'down' : 'stable';
    
    return { rate: Math.abs(rate), trend };
  }

  getPriorityColor(priority: string): string {
    const colors = {
      low: 'text-blue-600',
      medium: 'text-yellow-600',
      high: 'text-orange-600',
      critical: 'text-red-600'
    };
    return colors[priority as keyof typeof colors] || 'text-gray-600';
  }

  getStatusBadge(status: string): { label: string; color: string } {
    const statusMap: Record<string, { label: string; color: string }> = {
      active: { label: 'Active', color: 'bg-green-100 text-green-800' },
      inactive: { label: 'Inactive', color: 'bg-gray-100 text-gray-800' },
      pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-800' },
      approved: { label: 'Approved', color: 'bg-green-100 text-green-800' },
      rejected: { label: 'Rejected', color: 'bg-red-100 text-red-800' },
      confirmed: { label: 'Confirmed', color: 'bg-blue-100 text-blue-800' },
      cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-800' },
      completed: { label: 'Completed', color: 'bg-green-100 text-green-800' },
    };
    return statusMap[status] || { label: status, color: 'bg-gray-100 text-gray-800' };
  }

  // Orders Management
  async getAllOrders(params?: {
    page?: number;
    limit?: number;
    status?: 'pending_payment' | 'reviewing_payment' | 'paid' | 'processing' | 'shipped' | 'out_for_delivery' | 'completed' | 'cancelled';
    payment_method?: 'bank_transfer' | 'cod' | 'online';
    receipt_status?: 'pending' | 'approved' | 'rejected';
    start_date?: string;
    end_date?: string;
  }): Promise<ApiResponse<{
    orders: Array<{
      id: string;
      user: {
        id: string;
        name: string;
        email: string;
        phone: string;
      };
      total_amount: string;
      payment_method: string;
      status: string;
      receipt_status: string;
      receipt_url?: string;
      address: string;
      phone: string;
      items_count: number;
      items_summary: Array<{
        id: string;
        quantity: number;
        price: string;
        subtotal: string;
      }>;
      created_at: string;
      updated_at: string;
    }>;
    pagination: {
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    };
  }>> {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value.toString());
        }
      });
    }
    const queryString = queryParams.toString();
    const endpoint = queryString ? `/admin/orders?${queryString}` : '/admin/orders';
    return apiClient.get(endpoint);
  }

  async getOrderById(orderId: string): Promise<ApiResponse<any>> {
    return apiClient.get(`/admin/orders/${orderId}`);
  }

  async updateOrderStatus(orderId: string, data: {
    status: string;
    remarks?: string;
  }): Promise<ApiResponse<any>> {
    return apiClient.put(`/admin/orders/${orderId}/status`, data);
  }

  async approveReceipt(orderId: string): Promise<ApiResponse<any>> {
    return apiClient.put(`/admin/orders/${orderId}/receipt/approve`, {});
  }

  async rejectReceipt(orderId: string, data: {
    remarks: string;
  }): Promise<ApiResponse<any>> {
    return apiClient.put(`/admin/orders/${orderId}/receipt/reject`, data);
  }
}

// Export singleton instance
export const adminService = new AdminService();
export default adminService;
